import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useState} from 'react';
import {Node} from 'reactflow';

import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import FlowchartContextPanel, {
  PanelSide,
  SidepanelSize,
  sizeToWidth,
} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';
import FlowchartSidebar from 'ui-library/reactflow/sidepanels/FlowchartSidebar';

import {ProcessTaskSchemaGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import AddNodeSidepanelContent from './left-sidepanel/AddNodeSidepanelContent';
import {UserFacingNodeTypes} from './processFlowchartConfigEditorConstants';
import {TaskSchemaNodeData} from './processFlowchartConfigEditorNodeTypes';
import {FlowchartOptions} from './processFlowchartConfigEditorUtils';
import {ConfigurableTaskDefinitionGQL} from './useAllSchemasById';

export const LEFT_SIDEPANEL_SIZE = SidepanelSize.EXTRA_SMALL;
export const LEFT_SIDEPANEL_WIDTH = sizeToWidth[LEFT_SIDEPANEL_SIZE] as number;
interface LeftSidepanelContent {
  icon: BenchlingIconName;
  name: string;
  content: JSX.Element;
}

interface LeftSidepanelProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  allowedNodeTypes: Array<UserFacingNodeTypes>;
  taskDefinitionsForSidepanel: Array<ConfigurableTaskDefinitionGQL>;
  options: FlowchartOptions;
  addNodeToCanvas: (newNode: Node<TaskSchemaNodeData>) => void;
  rootTaskSchema: ProcessTaskSchemaGraphQL;
  flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator;
}

// TODO(BNCH-126721): Consolidate with transition modal left sidepanel
export default function ProcessFlowchartConfigEditorLeftSidepanel(props: LeftSidepanelProps): JSX.Element {
  const [leftSidepanelContent, setLeftSidepanelContent] = useState<LeftSidepanelContent | null>(null);
  const [selectedSidebarIconIndex, setSelectedSidebarIconIndex] = useState<number | null>(null);

  const setTaskNodeSidepanel = useCallback(() => {
    setLeftSidepanelContent({
      icon: 'process_task_schema',
      name: 'Add Nodes',
      content: (
        <AddNodeSidepanelContent
          allowedNodeTypes={props.allowedNodeTypes}
          taskDefinitionsForSidepanel={props.taskDefinitionsForSidepanel}
          addNodeToCanvas={props.addNodeToCanvas}
          options={props.options}
          rootTaskSchema={props.rootTaskSchema}
          flowchartEditorConfigurator={props.flowchartEditorConfigurator}
        />
      ),
    });
  }, [props]);

  // Rerender the left sidepanel when the available nodes change (based on dropping a node)
  useEffect(() => {
    setTaskNodeSidepanel();
  }, [props.taskDefinitionsForSidepanel]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={css(styles.leftSidepanelContainer)}>
      <FlowchartSidebar
        icons={[
          {
            name: 'process_task_schema',
            onClick: () => {
              setTaskNodeSidepanel();
              props.setIsOpen(true);
            },
            tooltip: 'Add Nodes',
          },
        ]}
        selectedIconIndex={selectedSidebarIconIndex}
        setSelectedIconIndex={setSelectedSidebarIconIndex}
      />
      <FlowchartContextPanel
        panelSide={PanelSide.LEFT}
        size={LEFT_SIDEPANEL_SIZE}
        sidepanelName={leftSidepanelContent?.name ?? ''}
        sidepanelIcon={leftSidepanelContent?.icon ?? 'wand-magic-sparkles'}
        isOpen={props.isOpen}
        onClose={() => {
          props.setIsOpen(false);
          setLeftSidepanelContent(null);
          setSelectedSidebarIconIndex(null);
        }}
      >
        {leftSidepanelContent?.content}
      </FlowchartContextPanel>
    </div>
  );
}

const styles = StyleSheet.create({
  leftSidepanelContainer: {
    display: 'flex',
    flexDirection: 'row',
    position: 'absolute',
    height: '100%',
    zIndex: 10,
  },
});
